import React, { createContext, useState, useEffect, useCallback } from 'react';
import promptService from '../services/promptService';
// Import the original notification hook to maintain compatibility
import useNotification from '../hooks/useNotification';

export const PromptContext = createContext();

export const PromptProvider = ({ children }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();

  // Fetch all prompts
  const fetchPrompts = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.getAllPrompts(filters);
      setPrompts(data);
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch prompts';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      return [];
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Fetch a specific prompt by ID
  const fetchPromptById = useCallback(async (promptId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.getPromptById(promptId);
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      return null;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Create a new prompt
  const createPrompt = useCallback(async (promptData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.createPrompt(promptData);
      setPrompts(prevPrompts => [data, ...prevPrompts]);
      showNotification({ type: 'success', message: 'Prompt created successfully' });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Update an existing prompt
  const updatePrompt = useCallback(async (promptId, promptData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.updatePrompt(promptId, promptData);
      setPrompts(prevPrompts => 
        prevPrompts.map(prompt => 
          prompt._id === promptId ? { ...prompt, ...data } : prompt
        )
      );
      showNotification({ type: 'success', message: 'Prompt updated successfully' });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Delete a prompt
  const deletePrompt = useCallback(async (promptId) => {
    setLoading(true);
    setError(null);
    try {
      await promptService.deletePrompt(promptId);
      setPrompts(prevPrompts => prevPrompts.filter(prompt => prompt._id !== promptId));
      showNotification({ type: 'success', message: 'Prompt deleted successfully' });
      return true;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Generate prompt using AI
  const generatePrompt = useCallback(async (promptData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.generatePrompt(promptData);
      showNotification({ type: 'success', message: 'Prompt generated successfully' });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Improve an existing prompt
  const improvePrompt = useCallback(async (promptId, improvementData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.improvePrompt(promptId, improvementData);
      showNotification({ type: 'success', message: 'Prompt improved successfully' });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to improve prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Templatize an existing prompt
  const templatizePrompt = useCallback(async (promptId, templateData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.templatizePrompt(promptId, templateData);
      showNotification({ type: 'success', message: 'Prompt templatized successfully' });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to templatize prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Evaluate a prompt
  const evaluatePrompt = useCallback(async (promptData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptService.evaluatePrompt(promptData);
      showNotification({ type: 'success', message: 'Prompt evaluation complete' });
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to evaluate prompt';
      setError(errorMessage);
      showNotification({ type: 'error', message: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Initial load of prompts - disabled to prevent "failed to fetch prompt" errors
  // useEffect(() => {
  //   fetchPrompts();
  // }, [fetchPrompts]);

  return (
    <PromptContext.Provider
      value={{
        prompts,
        loading,
        error,
        fetchPrompts,
        fetchPromptById,
        createPrompt,
        updatePrompt,
        deletePrompt,
        generatePrompt,
        improvePrompt,
        templatizePrompt,
        evaluatePrompt
      }}
    >
      {children}
    </PromptContext.Provider>
  );
};

export default PromptProvider;